import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import type { Card, CardBrand } from '@asia/shared';
import { haptic } from '../../hooks/useTelegram';
import { useBackHandler } from '../../hooks/useBackHandler';
import { useSheetTransition, SHEET_MS } from '../../hooks/useSheetTransition';
import { useCardAccount } from '../../hooks/useCardsApi';
import { useBalances } from '../../hooks/useChain';
import { useWallets } from '../../store/wallets';
import { fmtUsd } from '../../lib/format';
import type { CardTemplate } from '../../lib/cards';
import { ApiError, rpcErrorCode } from '../../lib/api';
import { ensureCardAccountApi } from '../../lib/cards';
import { FigIcon } from '../FigIcon';
import { CloseIcon } from '../icons';
import { Sheet } from './Sheet';
import { Dialog } from './Dialog';
import cardTile from '../../assets/figma/cards/card-tile.png';
import cardFaceImg from '../../assets/figma/cards/card-face.png';
import mastercard from '../../assets/icons/mastercard.svg';
import visaWhite from '../../assets/icons/visa-white.svg';

const brandName = (card: Card) => (card.brand === 'mastercard' ? 'Mastercard' : 'VISA');

/** App-icon style tile — Figma 2:6787: 122×87 rounded-19 card art with the
 *  brand mark centered; brand follows `card.brand`. */
export function CardTile({ card }: { card: Card }) {
  return (
    <div className="relative h-[87px] w-[122px]">
      <img src={cardTile} alt="" className="absolute inset-0 h-full w-full" />
      <img
        src={card.brand === 'mastercard' ? mastercard : visaWhite}
        alt={brandName(card)}
        className="absolute left-1/2 top-1/2 h-[26px] w-auto -translate-x-1/2 -translate-y-1/2"
      />
    </div>
  );
}

/** Apple Pay / Google Pay support. Which wallets a product actually works with
 *  differs per card, so this reports the template's own answer rather than
 *  promising both. */
function PaySupport({ template }: { template?: CardTemplate | null }) {
  const { t } = useTranslation();
  const mark = (ok: boolean) =>
    ok ? (
      <FigIcon name="pfCheck" size={20} className="text-positive" />
    ) : (
      <span className="text-[15px] font-medium text-faint">{t('cards.notSupported')}</span>
    );
  return (
    <div className="flex w-full flex-col gap-3 rounded-[24px] bg-card p-4">
      <Row label="Apple Pay" value={mark(template?.supportsApplePay ?? false)} />
      <div className="h-px w-full bg-hairline" />
      <Row label="Google Pay" value={mark(template?.supportsGooglePay ?? false)} />
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex w-full items-center justify-between">
      <span className="text-[15px] text-faint">{label}</span>
      <span className="text-right text-[15px] font-medium text-ink">{value}</span>
    </div>
  );
}

/** Issue fee + required min deposit (matches server issueTotalUsd). */
function issuePayTotal(t: CardTemplate): number {
  const dep = t.requiresInitialDeposit ? Math.max(0, t.minDepositUsd ?? t.minTopupUsd ?? 0) : 0;
  return Math.round((t.priceUsd + dep) * 100) / 100;
}

/** The fee / limit table (Figma node 2:6335), filled from the product itself —
 *  every figure here is what the user will actually be charged or allowed. */
function FeeTable({ template }: { template?: CardTemplate | null }) {
  const { t } = useTranslation();
  const cap = (v: number | null) => (v == null ? t('cards.noLimit') : `${fmtUsd(v)} USDT`);
  const dep =
    template?.requiresInitialDeposit
      ? Math.max(0, template.minDepositUsd ?? template.minTopupUsd ?? 0)
      : 0;
  const rows: { label: string; value: string }[] = template
    ? [
        { label: t('cards.issueFee'), value: `${fmtUsd(template.priceUsd)} USDT` },
        ...(dep > 0 ? [{ label: t('cards.issueDeposit'), value: `${fmtUsd(dep)} USDT` }] : []),
        ...(template.issueNetworkFeeHidden
          ? [{ label: t('send.fee'), value: `${fmtUsd(0)} USDT` }]
          : []),
        {
          label: t('cards.feeTopUp'),
          value: `${template.topupFeePct ?? template.projectTopupFeeUsd ?? 3}%`,
        },
        { label: t('cards.feeMinTopUp'), value: cap(template.minTopupUsd ?? 20) },
        { label: t('cards.feeKyc'), value: t('cards.notRequired') },
        { label: t('cards.feeSubscription'), value: t('cards.subscriptionValue') },
        { label: t('cards.feeTxLimit'), value: cap(template.limitPerTxUsd) },
        { label: t('cards.feeDailyLimit'), value: cap(template.limitPerDayUsd) },
        { label: t('cards.feeMonthlyLimit'), value: cap(template.limitPerMonthUsd) },
      ]
    : [];
  if (!rows.length) return null;
  return (
    <div className="flex w-full flex-col gap-3 rounded-[24px] bg-card p-4">
      {rows.map((r, i) => (
        <div key={r.label} className="flex flex-col gap-3">
          {i > 0 && <div className="h-px w-full bg-hairline" />}
          <Row label={r.label} value={r.value} />
        </div>
      ))}
    </div>
  );
}

/** The brand mark a product carries. */
const templateBrand = (tpl: CardTemplate): CardBrand =>
  /master/i.test(tpl.paymentSystem ?? '') ? 'mastercard' : 'visa';

/** Clean preview card for the issue sheet (Figma 2:6311) — just the card art
 *  and the brand mark; no balance, no card number (the card doesn't exist yet). */
function PreviewCardFace({ brand }: { brand: CardBrand }) {
  return (
    <div className="relative aspect-[370/216] w-full rounded-[36px] shadow-balance">
      <img src={cardFaceImg} alt="" className="absolute inset-0 h-full w-full" />
      <div className="absolute right-[5.4%] top-[9.3%] flex h-[15.3%] items-center">
        <img
          src={brand === 'mastercard' ? mastercard : visaWhite}
          alt={brand === 'mastercard' ? 'Mastercard' : 'VISA'}
          className="h-full w-auto"
        />
      </div>
    </div>
  );
}

/** Small card tile for the collapsed issue-sheet header — Figma 2:6581:
 *  48×38, radius 8, brand mark 37×12 centered. */
function MiniTile({ brand }: { brand: CardBrand }) {
  return (
    <div className="relative h-[38px] w-[48px] shrink-0 overflow-hidden rounded-[8px]">
      <img src={cardTile} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <img
        src={brand === 'mastercard' ? mastercard : visaWhite}
        alt={brand === 'mastercard' ? 'Mastercard' : 'VISA'}
        className="absolute left-1/2 top-1/2 h-[12px] w-auto -translate-x-1/2 -translate-y-1/2"
      />
    </div>
  );
}

function mapEmailVerifyError(t: (k: string) => string, code: string): string {
  const map: Record<string, string> = {
    email_in_use: t('cards.emailInUse'),
    email_required: t('cards.emailInvalid'),
    email_invalid: t('cards.emailInvalid'),
    provider_unavailable: t('cards.unavailable'),
    provider_registration_failed: t('cards.emailProviderFailed'),
    // Country is assigned by the provider — never blame the user for it.
    unsupported_country: t('cards.emailVerifyFailed'),
  };
  return map[code] ?? t('cards.emailVerifyFailed');
}

/** Issue-card sheet — Figma "Оформление карты" (2:6102; scrolled state 2:6373).
 *  - The hero (title + card + name/desc) scrolls away; past it a compact sticky
 *    header fades in (mini tile + title), matching the scrolled Figma frame.
 *  - The blue CTA is sticky at the bottom — content scrolls beneath it.
 *  - The card preview is a horizontal snap slider across the products actually
 *    on sale; the fees, limits and price below follow the selected one, and
 *    that is the card the user gets. */
export function IssueCardSheet({
  templates,
  onClose,
  onIssue,
}: {
  /** Everything currently on sale, straight from the provider. */
  templates: CardTemplate[];
  onClose: () => void;
  onIssue: (templateId: number, email?: string) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Smooth open/close. This sheet keeps its own hero-collapse gesture, so it
  // manages the slide + backdrop transition directly (rather than via <Sheet>).
  const { visible, dismiss } = useSheetTransition(onClose);

  // Swipe-down-to-dismiss from the grab handle at the very top (the body has its
  // own hero-collapse gesture, so dismiss is handle-only).
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(0);
  const startYRef = useRef(0);
  const downRef = useRef(false);
  const close = () => {
    setDragging(false);
    setDragY(0);
    dismiss();
  };
  useBackHandler(true, close);
  const onHandleDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    downRef.current = true;
    startYRef.current = e.clientY;
    dragRef.current = 0;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };
  const onHandleMove = (e: React.PointerEvent) => {
    if (!downRef.current) return;
    const dy = e.clientY - startYRef.current;
    if (dy > 4) setDragging(true);
    dragRef.current = dy > 0 ? dy : 0;
    setDragY(dragRef.current);
  };
  const onHandleUp = () => {
    if (!downRef.current) return;
    downRef.current = false;
    if (dragRef.current > 90) close();
    else {
      setDragging(false);
      setDragY(0);
    }
    dragRef.current = 0;
  };
  const [typeIdx, setTypeIdx] = useState(0);
  const [issueEmail, setIssueEmail] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // Scrolling the details region collapses the hero into the compact header.
  // Expanding is deliberately NOT scroll-driven (collapsing grows the scroll
  // area, which can snap scrollTop back to 0 and would bounce the hero open) —
  // instead the hero re-opens only on an explicit pull-down while at the top.
  const onSheetScroll = () => {
    const el = scrollRef.current;
    if (el && el.scrollTop > 40) setCollapsed(true);
  };
  // Gestures are handled on the whole sheet so the hero area (outside the
  // scroll region) also responds: scrolling down anywhere collapses the hero;
  // pulling down while the details are at the top expands it again.
  const atTop = () => (scrollRef.current?.scrollTop ?? 0) <= 0;
  const onWheel = (e: React.WheelEvent) => {
    if (!collapsed && e.deltaY > 8) setCollapsed(true);
    else if (collapsed && atTop() && e.deltaY < -8) setCollapsed(false);
  };
  const touchY = useRef(0);
  const onTouchStart = (e: React.TouchEvent) => {
    touchY.current = e.touches[0]?.clientY ?? 0;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const dy = (e.touches[0]?.clientY ?? 0) - touchY.current;
    if (!collapsed && dy < -24) setCollapsed(true);
    else if (collapsed && atTop() && dy > 24) setCollapsed(false);
  };
  const onTrackScroll = () => {
    const el = trackRef.current;
    const first = el?.firstElementChild as HTMLElement | null;
    if (!el || !first) return;
    const step = first.offsetWidth + 8; // slide + gap-2
    setTypeIdx(Math.max(0, Math.min(templates.length - 1, Math.round(el.scrollLeft / step))));
  };
  const sel = templates[Math.min(typeIdx, Math.max(0, templates.length - 1))] ?? null;
  const brand: CardBrand = sel ? templateBrand(sel) : 'visa';
  const needsEmail = !!sel?.requiresEmail;
  const emailTrimmed = issueEmail.trim();
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed);
  const { data: account } = useCardAccount(sel?.templateId);
  const accStatus = account?.status ?? 'none';
  const regEmail = account?.email?.trim().toLowerCase() ?? '';
  const emailApproved =
    !needsEmail ||
    (accStatus === 'approved' && !!regEmail && regEmail === emailTrimmed.toLowerCase());

  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // Same wallet the pay step will debit — gate the CTA before PIN / create.
  const wallet = useWallets((s) => s.wallets)[0];
  const usdtBal = useBalances(wallet?.address).data?.usdt;
  const issueNeed = sel ? issuePayTotal(sel) : 0;
  const insufficient =
    typeof usdtBal === 'number' && issueNeed > 0 && usdtBal + 1e-9 < issueNeed;

  const onVerifyEmail = async () => {
    if (!sel || !emailOk || verifyBusy) return;
    haptic();
    setVerifyError(null);
    setVerifyBusy(true);
    try {
      const res = await ensureCardAccountApi(sel.templateId, {
        email: emailTrimmed,
        waitForApproval: true,
        forceReregister: accStatus === 'rejected',
      });
      await qc.invalidateQueries({ queryKey: ['cardAccount', sel.templateId] });
      if (res.status === 'rejected') {
        setVerifyError(res.rejectionMessage ?? t('cards.emailRejected'));
      }
    } catch (e) {
      const code = rpcErrorCode(e);
      setVerifyError(
        code === 'email_in_use' || (e instanceof ApiError && e.status === 409)
          ? t('cards.emailInUse')
          : mapEmailVerifyError(t, code),
      );
    } finally {
      setVerifyBusy(false);
    }
  };

  const canIssue = !!sel && (!needsEmail || emailOk) && emailApproved && !insufficient;

  return createPortal(
    <div className="fixed inset-0 z-[60]">
      {/* dim backdrop */}
      <div
        onClick={close}
        className={`absolute inset-0 bg-black/[0.32] transition-opacity ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ transitionDuration: `${SHEET_MS}ms` }}
      />
      {/* sliding wrapper: grab handle + glass sheet move together */}
      <div
        className={`absolute inset-x-0 bottom-0 mx-auto flex max-w-[440px] flex-col items-center transition-transform ease-out ${
          visible ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={
          dragging
            ? { transform: `translateY(${dragY}px)`, transition: 'none' }
            : { transitionDuration: `${SHEET_MS}ms` }
        }
      >
        {/* grab handle — swipe down here to dismiss the whole sheet */}
        <div
          onPointerDown={onHandleDown}
          onPointerMove={onHandleMove}
          onPointerUp={onHandleUp}
          onPointerCancel={onHandleUp}
          className="mb-[6px] flex h-6 w-[64px] shrink-0 cursor-grab touch-none items-center justify-center"
        >
          <span className="h-[6px] w-full rounded-full bg-card/[0.67]" />
        </div>
        {/* Rounded/clip lives on THIS element — which has NO backdrop-filter — so
            iOS clips the corners reliably. The glass fill (Figma 2:6302) is a
            separate inner layer, clipped to the rounded top by this parent. */}
        <div
          onWheel={onWheel}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          className="relative z-10 h-[85vh] w-full overflow-hidden rounded-t-[36px]"
        >
          {/* glass background: rgba(0,0,0,0.16) fill + 40px blur */}
          <div className="pointer-events-none absolute inset-0 bg-black/[0.16] backdrop-blur-[40px]" />

          {/* content */}
          <div className="relative z-10 flex h-full flex-col">
          {/* close */}
          <button
            onClick={close}
            aria-label={t('common.close')}
            className="absolute right-4 top-4 z-30 grid size-9 place-items-center rounded-full text-ink active:scale-95"
            style={{ backgroundColor: 'rgb(var(--sheet) / 0.8)' }}
          >
            <CloseIcon width={20} height={20} />
          </button>
        {/* Collapsing app-bar: the hero and the compact header live OUTSIDE the
            scroll area and swap by animating their heights — the details panel
            always sits directly beneath whichever is visible, so there's never
            a gap between them (Figma 2:6102 ↔ 2:6373). */}

        {/* compact header — grows in when collapsed */}
        <div
          className="grid shrink-0 transition-[grid-template-rows,opacity] duration-300"
          style={{ gridTemplateRows: collapsed ? '1fr' : '0fr', opacity: collapsed ? 1 : 0 }}
        >
          <div className="overflow-hidden">
            <div className="flex h-[68px] items-center gap-[6px] rounded-[36px] bg-surface px-4">
              <MiniTile brand={brand} />
              <p className="flex-1 text-[16px] font-medium text-ink">{t('cards.issueTitle')}</p>
              <span className="w-9" />
            </div>
            <div className="h-[6px]" />
          </div>
        </div>

        {/* hero — collapses away when scrolled */}
        <div
          className="grid shrink-0 transition-[grid-template-rows,opacity] duration-300"
          style={{ gridTemplateRows: collapsed ? '0fr' : '1fr', opacity: collapsed ? 0 : 1 }}
        >
          <div className="overflow-hidden">
            <div className="flex flex-col items-center gap-6 rounded-[36px] bg-surface px-4 pb-6 pt-4">
              <p className="py-[10px] text-[16px] font-medium text-ink">{t('cards.issueTitle')}</p>

              {/* card-type slider — same scale as the Cards screen: full content
                  width per slide, 8px gap, next type peeks 8px on the right. */}
              <div
                ref={trackRef}
                onScroll={onTrackScroll}
                className="no-scrollbar -mx-4 -my-12 flex snap-x snap-mandatory gap-2 self-stretch overflow-x-auto scroll-px-4 px-4 py-12"
              >
                {(templates.length ? templates : [null]).map((tpl, i) => (
                  <div key={tpl?.templateId ?? i} className="w-full shrink-0 snap-start">
                    <PreviewCardFace brand={tpl ? templateBrand(tpl) : 'visa'} />
                  </div>
                ))}
              </div>

              <div className="flex flex-col items-center gap-2 text-center">
                <p className="text-[18px] font-medium text-ink">
                  {sel?.name ?? t('cards.unavailable')}
                </p>
                <p className="text-[15px] leading-[1.44] text-muted">
                  {sel
                    ? t([`cards.desc.${sel.templateId}`, 'cards.cardDesc'], {
                        brand: brand === 'mastercard' ? 'Mastercard' : 'VISA',
                      })
                    : t('cards.cardDesc', {
                        brand: brand === 'mastercard' ? 'Mastercard' : 'VISA',
                      })}
                </p>
              </div>
            </div>
            <div className="h-[6px]" />
          </div>
        </div>

        {/* details — the only scrolling region. The top rounding lives on the
            CONTAINER so the corners stay rounded no matter how far the panel
            inside has scrolled (the panel's own rounding scrolls away). */}
        <div
          ref={scrollRef}
          onScroll={onSheetScroll}
          className="no-scrollbar flex-1 overflow-y-auto rounded-t-[36px]"
        >
          {/* min-h-full: the panel always fills the scroll viewport so no glass
              band shows below it at the sheet bottom. */}
          <div className="flex min-h-full flex-col gap-3 rounded-t-[36px] bg-surface px-4 pb-32 pt-4">
            {needsEmail && (
              <div className="flex w-full flex-col gap-2 rounded-[24px] bg-card p-4">
                <label htmlFor="card-issue-email" className="text-[15px] text-faint">
                  {t('cards.emailLabel')}
                </label>
                <input
                  id="card-issue-email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={issueEmail}
                  onChange={(e) => setIssueEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="h-[44px] rounded-[14px] bg-surface px-3 text-[15px] text-ink outline-none"
                />
                <p className="text-[13px] leading-[1.4] text-muted">{t('cards.emailForCard')}</p>
                {emailTrimmed.length > 0 && !emailOk && (
                  <p className="text-[13px] text-negative">{t('cards.emailInvalid')}</p>
                )}
                <button
                  type="button"
                  onClick={() => void onVerifyEmail()}
                  disabled={!emailOk || verifyBusy || accStatus === 'approved'}
                  className="mt-1 flex h-[44px] w-full items-center justify-center rounded-full border border-accent bg-card text-[15px] font-semibold text-accent disabled:opacity-50"
                >
                  {verifyBusy
                    ? t('cards.emailVerifying')
                    : accStatus === 'approved'
                      ? t('cards.emailVerified')
                      : t('cards.emailVerify')}
                </button>
                {accStatus === 'pending' && (
                  <p className="text-[13px] text-muted">{t('cards.emailPending')}</p>
                )}
                {(verifyError || (accStatus === 'rejected' && account?.rejectionMessage)) && (
                  <p className="text-[13px] text-negative">
                    {verifyError ?? account?.rejectionMessage}
                  </p>
                )}
              </div>
            )}
            <PaySupport template={sel} />
            <FeeTable template={sel} />
            <p className="px-1 text-[13px] leading-[1.4] text-faint">{t('cards.issuePayOnly')}</p>
          </div>
        </div>

        {/* sticky CTA — floats over the scrolling content */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 px-6 pb-8">
          {insufficient && (
            <p className="pointer-events-none mb-2 text-center text-[13px] text-negative">
              {t('cards.insufficient')}
            </p>
          )}
          <button
            onClick={() => {
              if (!sel || !canIssue) return;
              haptic();
              onIssue(sel.templateId, needsEmail ? emailTrimmed : undefined);
            }}
            disabled={!canIssue}
            className="pointer-events-auto flex h-[52px] w-full items-center justify-center rounded-full bg-accent text-[15px] font-bold text-white shadow-[0px_8px_24px_rgba(33,149,241,0.35)] active:opacity-90 disabled:opacity-60"
          >
            {sel ? t('cards.issueFor', { price: fmtUsd(issuePayTotal(sel)) }) : t('cards.unavailable')}
          </button>
        </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Delete-card confirmation dialog — Figma "Удалить карту?" (2:5926 overlay). */
export function DeleteCardDialog({
  card,
  onConfirm,
  onCancel,
}: {
  card: Card;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog onDismiss={onCancel}>
      {(close) => (
        // Same dimensions / layout as RenameCardDialog: 338-wide glass card with
        // the title / tile / actions in a gap-6 stack.
        <div
          className="relative flex w-[338px] flex-col items-center rounded-[36px] p-4 backdrop-blur-[2px]"
          style={{ backgroundColor: 'rgb(var(--surface) / 0.9)' }}
        >
          <button
            onClick={() => close(onCancel)}
            aria-label={t('common.close')}
            className="absolute right-4 top-4 z-10 grid size-9 place-items-center rounded-full text-ink active:scale-95"
            style={{ backgroundColor: 'rgb(var(--sheet) / 0.8)' }}
          >
            <CloseIcon width={20} height={20} />
          </button>
          <div className="relative flex w-full flex-col items-center gap-6">
            <div className="flex h-9 items-center justify-center">
              <p className="w-[276px] text-center text-[18px] font-medium leading-[1.32] text-ink">
                {t('cards.deleteConfirm') ?? 'Delete card?'}
              </p>
            </div>

            <div className="flex w-full flex-col items-center gap-4">
              <CardTile card={card} />
              <div className="flex h-[44px] w-full flex-col items-center justify-center gap-2">
                <span className="text-[24px] font-medium leading-none text-ink">{card.label}</span>
                <div className="flex items-center gap-[5px] text-[13px] leading-none text-muted">
                  **** {card.last4}
                  <span className="inline-block size-[2px] rounded-full bg-muted" />
                  {brandName(card)}
                </div>
              </div>
            </div>

            <div className="flex w-full items-start gap-2">
              <button
                onClick={() => close(onCancel)}
                className="flex h-[52px] flex-[1_0_0] items-center justify-center rounded-full border border-card p-4 active:scale-[0.98]"
                style={{ backgroundColor: 'rgb(var(--press) / 0.08)' }}
              >
                <span className="text-[15px] font-bold leading-none text-ink">
                  {t('common.cancel')}
                </span>
              </button>
              <button
                onClick={() => {
                  haptic('medium');
                  close(onConfirm);
                }}
                className="flex h-[52px] flex-[1_0_0] items-center justify-center rounded-full border border-card bg-card p-4 active:scale-[0.98]"
              >
                <span className="text-[15px] font-bold leading-none text-negative">
                  {t('common.delete')}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/** Card-management ("settings") sheet: tile + inline-editable name, the card
 *  info / fee table, and a destructive Delete button pinned at the bottom.
 *  Renaming happens in place (pencil next to the name → input); there is no
 *  separate details/rename action row. */
export function CardManageSheet({
  card,
  template,
  onClose,
  onRename,
  onDelete,
}: {
  card: Card;
  /** The product this card was issued from — its real fees and limits. */
  template?: CardTemplate | null;
  onClose: () => void;
  onRename: (label: string) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(card.label);
  const save = () => {
    const v = label.trim();
    if (v && v.length <= 20 && v !== card.label) onRename(v);
    else setLabel(card.label);
    setEditing(false);
  };
  return (
    <Sheet
      onClose={onClose}
      panelClassName="flex w-full flex-col items-center gap-6 rounded-t-[36px] bg-surface px-4 pb-[44px] pt-4"
    >
        <div className="flex w-full flex-col items-center gap-5">
          <div className="flex flex-col items-center gap-[6px]">
            <p className="text-[16px] font-medium text-ink">{t('cards.title')}</p>
            <p className="text-[14px] text-muted">{brandName(card)}</p>
          </div>

          <CardTile card={card} />

          <div className="flex min-h-[44px] w-full flex-col items-center justify-center gap-2">
            {editing ? (
              <div className="flex w-full items-center gap-2">
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value.slice(0, 20))}
                  maxLength={20}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') save();
                    else if (e.key === 'Escape') {
                      setLabel(card.label);
                      setEditing(false);
                    }
                  }}
                  className="min-w-0 flex-1 rounded-[12px] border border-border bg-card px-3 py-2 text-center text-[20px] font-medium leading-none text-ink caret-black outline-none"
                />
                <button
                  onClick={() => {
                    haptic('medium');
                    save();
                  }}
                  aria-label={t('common.save')}
                  className="grid size-10 shrink-0 place-items-center rounded-full bg-accent text-white active:opacity-90"
                >
                  <FigIcon name="pfCheck" size={18} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-[24px] font-medium leading-none text-ink">{card.label}</span>
                <button
                  onClick={() => {
                    haptic();
                    setLabel(card.label);
                    setEditing(true);
                  }}
                  aria-label={t('wallet.rename')}
                  className="grid size-7 place-items-center rounded-full bg-press/[0.05] active:opacity-70"
                >
                  <FigIcon name="edit2" size={15} className="text-muted" />
                </button>
              </div>
            )}
            <div className="flex items-center gap-[5px] text-[13px] leading-none text-muted">
              **** {card.last4}
              <span className="inline-block size-[2px] rounded-full bg-muted" />
              {brandName(card)}
            </div>
          </div>
        </div>

        {/* card info + fee/limit table */}
        <div className="flex w-full flex-col gap-3">
          <p className="px-4 text-[15px] font-medium text-muted">{t('cards.info')}</p>
          <PaySupport template={template} />
        </div>
        <FeeTable template={template} />

        {/* destructive delete — full-width red button pinned at the bottom */}
        <button
          onClick={() => {
            haptic();
            onDelete();
          }}
          className="flex h-[52px] w-full shrink-0 items-center justify-center rounded-full bg-negative text-[15px] font-bold text-white active:opacity-90"
        >
          {t('common.delete')}
        </button>
    </Sheet>
  );
}

/** Rename-card dialog — same centered card as RenameWalletDialog, with the
 *  card label editable in place (≤20 chars). */
export function RenameCardDialog({
  card,
  onConfirm,
  onCancel,
}: {
  card: Card;
  onConfirm: (label: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [label, setLabel] = useState(card.label);
  const valid = label.trim().length > 0 && label.trim().length <= 20;

  return (
    <Dialog onDismiss={onCancel}>
      {(close) => (
        <div
          className="relative flex w-[338px] flex-col items-center rounded-[36px] p-4 backdrop-blur-[2px]"
          style={{ backgroundColor: 'rgb(var(--surface) / 0.9)' }}
        >
          <button
            onClick={() => close(onCancel)}
            aria-label={t('common.close')}
            className="absolute right-4 top-4 z-10 grid size-9 place-items-center rounded-full text-ink active:scale-95"
            style={{ backgroundColor: 'rgb(var(--sheet) / 0.8)' }}
          >
            <CloseIcon width={20} height={20} />
          </button>
          <div className="relative flex w-full flex-col items-center gap-6">
            <div className="flex h-9 items-center justify-center">
              <p className="w-[276px] text-center text-[18px] font-medium leading-[1.32] text-ink">
                {t('cards.renameTitle')}
              </p>
            </div>

            <div className="flex w-full flex-col items-center gap-4">
              <CardTile card={card} />
              <div className="flex h-[44px] w-full flex-col items-center justify-center gap-2">
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value.slice(0, 20))}
                  maxLength={20}
                  autoFocus
                  className="w-full bg-transparent text-center text-[24px] font-medium leading-none text-ink outline-none placeholder:text-muted"
                  placeholder={t('cards.cardName')}
                />
                <div className="flex items-center gap-[5px] text-[13px] leading-none text-muted">
                  **** {card.last4}
                  <span className="inline-block size-[2px] rounded-full bg-muted" />
                  {brandName(card)}
                </div>
              </div>
            </div>

            <div className="flex w-full items-start gap-2">
              <button
                onClick={() => close(onCancel)}
                className="flex h-[52px] flex-[1_0_0] items-center justify-center rounded-full border border-card p-4 active:scale-[0.98]"
                style={{ backgroundColor: 'rgb(var(--press) / 0.08)' }}
              >
                <span className="text-[15px] font-bold leading-none text-ink">
                  {t('common.cancel')}
                </span>
              </button>
              <button
                onClick={() => {
                  if (!valid) return;
                  haptic('medium');
                  close(() => onConfirm(label.trim()));
                }}
                className="flex h-[52px] flex-[1_0_0] items-center justify-center rounded-full border border-card bg-accent p-4 active:scale-[0.98] disabled:opacity-50"
                disabled={!valid}
              >
                <span className="text-[15px] font-bold leading-none text-white">
                  {t('common.save')}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
